/**
 * SUB-5.8 · F-97. Read every row, or say you could not.
 *
 * ⚠️ THE BUG THIS CLOSES. `PortfolioRollup` read every project in scope with no
 * `.range()` and no `.limit()` anywhere in its path, so it took whatever ceiling
 * the API applies. Measured against the live REST API on 2026-08-17:
 *
 *     content-range: 0-1999/4870
 *
 * The API caps a collection response at 2,000 rows while 4,870 projects exist.
 * Rows past offset 2000 are real and reachable with explicit paging. So every
 * figure computed client-side from those rows was capped and rendered as a
 * perfectly plausible number, with nothing on the page or in the console saying
 * so.
 *
 * ⚠️ THE CEILING IS INVISIBLE TO SQL. SUB-5.7 already recorded this: "`max_rows`
 * is invisible to SQL, so PART J measures the paging SEMANTICS, not the cap."
 * That is correct and it is why a story that went looking right next to this
 * defect did not find it. The cap belongs to PostgREST, not to Postgres, so this
 * repo's standing rule to treat `pg_proc` and `pg_policies` as the source of
 * truth does not reach it. One REST request with `Prefer: count=exact` does.
 *
 * ⚠️ THE SORT IS APPLIED HERE, BY THIS FUNCTION, ON PURPOSE. There is no row
 * order in PostgreSQL without an `ORDER BY`, so paging by offset over an
 * unordered set can skip or duplicate rows as the underlying order shifts
 * between requests. That would trade one silent wrongness for another and be far
 * harder to spot. A caller cannot forget the sort because a caller never supplies
 * it: `readAllRows` takes the column and applies it itself.
 */

/**
 * The shape `readAllRows` needs from a query. Supabase's builder satisfies it
 * structurally, and so does a fake in a test, which is the point.
 */
export interface PageableQuery<T> {
  order(column: string, options: { ascending: boolean }): PageableQuery<T>;
  range(from: number, to: number): PromiseLike<{ data: T[] | null; error: unknown }>;
}

/**
 * Rows requested per page. Must stay BELOW the server's cap, currently 2,000.
 *
 * ⚠️ The loop does not depend on this being below the cap: it advances by the
 * number of rows actually RETURNED and stops only on an empty page, so a server
 * that returns fewer rows than asked for is handled rather than mistaken for the
 * end of the data. That is deliberate, because assuming the requested page size
 * came back is exactly how a lowered cap would silently reintroduce F-97.
 */
export const DEFAULT_PAGE_SIZE = 1000;

/**
 * A runaway guard, not a limit on the data. At the default page size this is
 * 500,000 rows, far beyond any real portfolio.
 *
 * ⚠️ EXCEEDING IT IS AN ERROR, NEVER A TRUNCATED SUCCESS. Returning the rows
 * gathered so far would be precisely the defect this file exists to close, in a
 * new place.
 */
export const MAX_PAGES = 500;

export interface PagedReadResult<T> {
  rows: T[];
  /** Non-null if the read failed. The rows are then not to be trusted. */
  error: unknown | null;
}

/**
 * Read every row a query matches, in pages, ordered.
 *
 * `makeQuery` is called once per page and must return a FRESH query, because a
 * Supabase builder is consumed when awaited.
 *
 * ⚠️ **"FRESH" IS A REQUIREMENT, NOT A STYLE NOTE, AND `order()` IS WHY.**
 * `PostgrestTransformBuilder.order()` APPENDS to the existing `order` parameter
 * (`searchParams.set(key, existing ? existing + ',' + next : next)`) while
 * `range()` overwrites. So handing back the same builder on every page builds
 * `order=id.asc,id.asc,id.asc…` and grows the URL once per request. Verified in
 * `node_modules/@supabase/postgrest-js` on 2026-08-17.
 */
export async function readAllRows<T>(
  makeQuery: () => PageableQuery<T>,
  options: { orderBy: string; pageSize?: number },
): Promise<PagedReadResult<T>> {
  return readAllRowsAsync(async () => ({ query: makeQuery() }), options);
}

/**
 * SUB-5.12 · The same read, when building a fresh query is itself asynchronous.
 *
 * ⚠️ **THE WRAPPER OBJECT IS NOT CEREMONY. A SUPABASE BUILDER IS A THENABLE, SO
 * `await` ON A PROMISE OF ONE EXECUTES THE QUERY** and resolves with a
 * `PostgrestSingleResponse` instead of the builder, per Promise/A+ resolution.
 * `{ query }` is a plain object, so awaiting it returns it as-is and the loop
 * below can still chain `.order()` and `.range()`.
 *
 * This is the same trap `DynamicWidgetQueryService.applyFilters` documents and
 * wraps against for the same reason. ⚠️ It was reintroduced here in a first
 * draft of SUB-5.12 with a bare `Promise<PageableQuery<T>>` and caught by
 * `tsc -b`, which is the only reason this paragraph exists rather than a silent
 * defect: at runtime the paging loop would have received a response object and
 * thrown on `.order`.
 *
 * `DynamicWidgetQueryService` needs it because rebuilding a widget query means
 * re-running `applyFilters`, which resolves "current user" filters against
 * `auth.getUser()`.
 */
export async function readAllRowsAsync<T>(
  makeQuery: () => Promise<{ query: PageableQuery<T> }>,
  options: { orderBy: string; pageSize?: number },
): Promise<PagedReadResult<T>> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const rows: T[] = [];
  let from = 0;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const { query } = await makeQuery();
    const { data, error } = await query
      .order(options.orderBy, { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) return { rows, error };

    const batch = data ?? [];
    rows.push(...batch);

    // ⚠️ TERMINATE ON AN EMPTY PAGE, not on a short one. A short page can mean
    // the end of the data OR a server cap lower than the page size, and those
    // are indistinguishable from here. Stopping on "shorter than I asked for"
    // would silently truncate against a lower cap. The cost is one extra request
    // that returns nothing, which is the correct price for not being wrong.
    if (batch.length === 0) return { rows, error: null };

    from += batch.length;
  }

  // Never reached by real data. If it is, something is wrong and saying so is
  // the only safe answer.
  return {
    rows,
    error: new Error(
      `readAllRows exceeded ${MAX_PAGES} pages (${rows.length} rows). Refusing to return a partial set.`,
    ),
  };
}
